import { useCallback, useEffect, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

function Attendance() {
  const { hasPermission } = useAuth();
  const [attendance, setAttendance] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDetailedModal, setShowDetailedModal] = useState(false);
  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showExportMenu, setShowExportMenu] = useState(false);
  const isAdmin = hasPermission('employees', 'read');

  const fetchAttendance = useCallback(async () => {
    try {
      let url = '/attendance';
      if (startDate && endDate) {
        url += `?startDate=${startDate}&endDate=${endDate}`;
      }
      const response = await api.get(url);
      setAttendance(response.data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const handleFilter = () => {
    fetchAttendance();
  };

  const handleExportExcel = async () => {
    try {
      let url = '/attendance/export/excel';
      if (startDate && endDate) {
        url += `?startDate=${startDate}&endDate=${endDate}`;
      }

      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Attendance_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
    } catch (error) {
      console.error('Error exporting excel:', error);
      alert('Error exporting Excel file');
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text('Attendance Report', 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 25);
    if (startDate && endDate) {
      doc.text(`Period: ${startDate} to ${endDate}`, 14, 30);
    }

    const tableColumn = isAdmin
      ? ["Date", "Employee", "Login Time", "Hours", "Status"]
      : ["Date", "Login Time", "Hours", "Status"];

    const tableRows = attendance.map(record => {
      const date = new Date(record.date).toLocaleDateString();
      const login = formatTime(record.loginTime);
      const hours = record.workingHours || (record.loginTime && !record.logoutTime ? Math.round((new Date() - new Date(record.loginTime)) / (1000 * 60 * 60) * 100) / 100 : 0);
      const status = record.status;

      if (isAdmin) {
        const empName = record.employee ? `${record.employee.firstName} ${record.employee.lastName}` : 'N/A';
        return [date, empName, login, hours, status];
      }
      return [date, login, hours, status];
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: startDate && endDate ? 35 : 30,
    });

    doc.save(`Attendance_Report.pdf`);
  };

  const handleExportWord = async () => {
    try {
      let url = '/attendance/export/word';
      if (startDate && endDate) {
        url += `?startDate=${startDate}&endDate=${endDate}`;
      }

      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Attendance_${new Date().toISOString().slice(0, 10)}.docx`;
      link.click();
    } catch (error) {
      console.error('Error exporting word:', error);
      alert('Error exporting Word file');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      present: 'badge-success',
      absent: 'badge-danger',
      'half-day': 'badge-warning'
    };
    return badges[status] || 'badge-info';
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    return new Date(time).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const handleViewDetails = async (employeeId) => {
    setLoadingDetails(true);
    setShowDetailedModal(true);

    try {
      const response = await api.get(`/payroll/employee-attendance/${employeeId}`, {
        params: { month: selectedMonth, year: selectedYear }
      });
      setDetailedData(response.data);
    } catch (error) {
      console.error('Error fetching detailed attendance:', error);
      const errorMessage = error.response?.data?.message || 'Error loading attendance details. Please try again.';
      alert(errorMessage);
      setShowDetailedModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <div className="container">
      <h1 style={{ fontSize: 'clamp(20px, 5vw, 28px)' }}>Attendance Records</h1>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, flex: '1 1 150px', minWidth: '150px' }}>
            <label>Start Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: '1 1 150px', minWidth: '150px' }}>
            <label>End Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          {isAdmin && (
            <>
              <div className="form-group" style={{ marginBottom: 0, flex: '1 1 120px', minWidth: '120px' }}>
                <label>Month</label>
                <select
                  className="form-control"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                >
                  {months.map(month => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0, flex: '1 1 100px', minWidth: '100px' }}>
                <label>Year</label>
                <select
                  className="form-control"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                >
                  {years.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          <button className="btn btn-primary" onClick={handleFilter}>
            Filter
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setStartDate('');
              setEndDate('');
              fetchAttendance();
            }}
          >
            Clear
          </button>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              style={{
                padding: '8px 14px',
                height: '38px',
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap'
              }}
            >
              Export
              <span style={{ fontSize: '10px' }}>▼</span>
            </button>
            {showExportMenu && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '5px',
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  minWidth: '180px'
                }}
              >
                <div
                  onClick={() => {
                    handleExportPDF();
                    setShowExportMenu(false);
                  }}
                  style={{
                    padding: '10px 15px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '13px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.background = 'white'}
                >
                  📄 Export as PDF
                </div>
                <div
                  onClick={() => {
                    handleExportExcel();
                    setShowExportMenu(false);
                  }}
                  style={{
                    padding: '10px 15px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '13px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.background = 'white'}
                >
                  📊 Export as Excel
                </div>
                <div
                  onClick={() => {
                    handleExportWord();
                    setShowExportMenu(false);
                  }}
                  style={{
                    padding: '10px 15px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.background = 'white'}
                >
                  📝 Export as Word
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isAdmin && attendance.length > 0 && (
        <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
          <h3 style={{ marginBottom: '10px' }}>Attendance Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
            <div>
              <p style={{ color: '#666', fontSize: '14px' }}>Present</p>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                {attendance.filter(a => a.status === 'present').length}
              </p>
            </div>
            <div>
              <p style={{ color: '#666', fontSize: '14px' }}>Absent</p>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
                {attendance.filter(a => a.status === 'absent').length}
              </p>
            </div>
            <div>
              <p style={{ color: '#666', fontSize: '14px' }}>Total Records</p>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
                {attendance.length}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              {isAdmin && <th>Employee</th>}
              <th>Date</th>
              <th>Login Time</th>
              <th>Working Hours</th>
              <th>Goals Created</th>
              <th>Goals Completed</th>
              <th>Status</th>
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {attendance.map((record) => (
              <tr key={record._id}>
                {isAdmin && (
                  <td>
                    {record.employee?.firstName} {record.employee?.lastName}
                    <br />
                    <small style={{ color: '#666' }}>({record.employee?.employeeId})</small>
                  </td>
                )}
                <td>{new Date(record.date).toLocaleDateString()}</td>
                <td>{formatTime(record.loginTime)}</td>
                <td>
                  {record.workingHours > 0
                    ? `${record.workingHours} hrs`
                    : record.loginTime
                      ? `${Math.round((new Date() - new Date(record.loginTime)) / (1000 * 60 * 60) * 100) / 100} hrs`
                      : 'N/A'}
                </td>
                <td style={{ textAlign: 'center' }}>{record.goalsCreated || 0}</td>
                <td style={{ textAlign: 'center' }}>{record.goalsCompleted || 0}</td>
                <td>
                  <span className={`badge ${getStatusBadge(record.status)}`}>
                    {record.status}
                  </span>
                </td>
                {isAdmin && (
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleViewDetails(record.employee?._id)}
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      View Details
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {attendance.length === 0 && (
          <p className="text-center" style={{ padding: '20px' }}>
            No attendance records found
          </p>
        )}
      </div>

      {/* Detailed Attendance Modal - Same as Payroll */}
      {showDetailedModal && detailedData && (
        <div className="modal-overlay" onClick={() => setShowDetailedModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-container" style={{ maxWidth: '95%', width: '1400px', background: 'white', borderRadius: '12px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>
                {detailedData.employee.name} - Attendance Details
              </h2>
              <button onClick={() => setShowDetailedModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>

            <div className="modal-body" style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              {loadingDetails ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p>Loading attendance details...</p>
                </div>
              ) : (
                <>
                  {/* Employee Info */}
                  <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                      <div>
                        <strong>Employee ID:</strong> {detailedData.employee.employeeId}
                      </div>
                      <div>
                        <strong>Department:</strong> {detailedData.employee.department || 'N/A'}
                      </div>
                      <div>
                        <strong>Designation:</strong> {detailedData.employee.designation || 'N/A'}
                      </div>
                      <div>
                        <strong>Month/Year:</strong> {months.find(m => m.value === detailedData.month)?.label} {detailedData.year}
                      </div>
                    </div>
                  </div>

                  {/* Daily Attendance Table */}
                  <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#286cd2', color: 'white' }}>
                          <th style={{ padding: '8px', border: '1px solid #ddd', minWidth: '40px' }}>Day</th>
                          {detailedData.dailyBreakdown.map((day) => (
                            <th key={day.day} style={{
                              padding: '8px',
                              border: '1px solid #ddd',
                              minWidth: '50px',
                              background: day.status === 'Absent' ? '#ffebee' : day.status === 'Half-day' ? '#fff3e0' : '#286cd2'
                            }}>
                              {day.day}
                            </th>
                          ))}
                          <th style={{ padding: '8px', border: '1px solid #ddd', minWidth: '80px' }}>Summary</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Active Time Row */}
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f8f9fa' }}>
                            Active Time
                          </td>
                          {detailedData.dailyBreakdown.map((day) => (
                            <td key={`time-${day.day}`} style={{
                              padding: '8px',
                              border: '1px solid #ddd',
                              textAlign: 'center',
                              fontSize: '11px'
                            }}>
                              {day.loginTime ? new Date(day.loginTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                          ))}
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                            {detailedData.summary.totalDays}
                          </td>
                        </tr>

                        {/* Dept Time Row */}
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f8f9fa' }}>
                            Dept Time
                          </td>
                          {detailedData.dailyBreakdown.map((day) => (
                            <td key={`dept-${day.day}`} style={{
                              padding: '8px',
                              border: '1px solid #ddd',
                              textAlign: 'center',
                              fontSize: '11px'
                            }}>
                              {day.logoutTime ? new Date(day.logoutTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-'}
                            </td>
                          ))}
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                            {detailedData.summary.presentDays}
                          </td>
                        </tr>

                        {/* Working Hrs Row */}
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f8f9fa' }}>
                            Working Hrs
                          </td>
                          {detailedData.dailyBreakdown.map((day) => (
                            <td key={`hrs-${day.day}`} style={{
                              padding: '8px',
                              border: '1px solid #ddd',
                              textAlign: 'center',
                              fontWeight: '500'
                            }}>
                              {day.workingHours > 0 ? day.workingHours.toFixed(1) : '-'}
                            </td>
                          ))}
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                            {detailedData.summary.halfDays}
                          </td>
                        </tr>

                        {/* On Time/Late Row */}
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f8f9fa' }}>
                            On Time/Late
                          </td>
                          {detailedData.dailyBreakdown.map((day) => (
                            <td key={`ontime-${day.day}`} style={{
                              padding: '8px',
                              border: '1px solid #ddd',
                              textAlign: 'center',
                              background: day.isLate ? '#ffebee' : day.status === 'Present' ? '#e8f5e9' : 'white',
                              fontWeight: '500',
                              fontSize: '11px'
                            }}>
                              {day.status === 'Present' ? (day.isLate ? 'Late' : 'On Time') : '-'}
                            </td>
                          ))}
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                            {detailedData.summary.absentDays}
                          </td>
                        </tr>

                        {/* Shift Row */}
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f8f9fa' }}>
                            Shift
                          </td>
                          {detailedData.dailyBreakdown.map((day) => (
                            <td key={`shift-${day.day}`} style={{
                              padding: '8px',
                              border: '1px solid #ddd',
                              textAlign: 'center',
                              background: day.status === 'Present' ? '#e3f2fd' : day.status === 'Absent' ? '#ffebee' : '#fff3e0',
                              fontWeight: 'bold',
                              fontSize: '11px'
                            }}>
                              {day.status === 'Present' ? 'P' : day.status === 'Half-day' ? 'H' : day.status === 'Absent' ? 'A' : 'OFF'}
                            </td>
                          ))}
                          <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#fff3e0' }}>
                            Late: {detailedData.summary.lateDays}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Payroll Summary */}
                  {detailedData.payroll && (
                    <div style={{ marginTop: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                      <h3 style={{ marginBottom: '15px', color: '#286cd2' }}>Salary Calculation</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #ddd' }}>
                            <span><strong>Basic Salary:</strong></span>
                            <span style={{ fontWeight: 'bold', fontSize: '18px' }}>{formatCurrency(detailedData.payroll.basicSalary)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #ddd' }}>
                            <span>Late Deduction:</span>
                            <span style={{ color: '#ef4444' }}>-{formatCurrency(detailedData.payroll.deductionBreakdown.lateDeduction)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #ddd' }}>
                            <span>Goal Deduction:</span>
                            <span style={{ color: '#ef4444' }}>-{formatCurrency(detailedData.payroll.deductionBreakdown.goalDeduction)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #ddd' }}>
                            <span>Loss of Pay (Absent):</span>
                            <span style={{ color: '#ef4444' }}>-{formatCurrency(detailedData.payroll.deductionBreakdown.lossOfPay)}</span>
                          </div>
                        </div>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #ddd' }}>
                            <span><strong>Total Deductions:</strong></span>
                            <span style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '18px' }}>-{formatCurrency(detailedData.payroll.totalDeductions)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px', background: '#e8f5e9', borderRadius: '8px', marginTop: '10px' }}>
                            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>Net Salary:</span>
                            <span style={{ color: '#10b981', fontWeight: 'bold', fontSize: '24px' }}>{formatCurrency(detailedData.payroll.netSalary)}</span>
                          </div>
                          <div style={{ marginTop: '15px', padding: '10px', background: '#fff3e0', borderRadius: '8px' }}>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              <div>Total Working Hours: <strong>{detailedData.summary.totalWorkingHours} hrs</strong></div>
                              <div>Present Days: <strong>{detailedData.summary.presentDays}</strong></div>
                              <div>Late Days: <strong>{detailedData.summary.lateDays}</strong></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDetailedModal(false)} className="btn btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Attendance;
